import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Fieldset, Field, Control, Button } from "rbx";

import { useMutation } from "react-apollo";
import { useAuthorization } from "../Authorization";
import { getUTCFormattedDate, ourToast as toast } from "../../utils";
import { useBookingCodeReversible } from "../../hooks";

import {
  UPDATE_BOOKING_MUTATION,
  DELETE_BOOKING_MUTATION
} from "../../graphql/Bookings/Bookings.graphql";

import BookingForm from "./BookingForm";
import { useCurrentUser } from "../../utils/hooks";
import { useModal } from "../ModalProvider";
import AddBookingForm from "./AddBookingForm";

const EditBookingForm = ({
  state: initialState,
  onComplete,
  isBookable,
  refetchQueries,
  bookingCodeWhere
}) => {
  const { data: user } = useCurrentUser();
  const { setModalOpen } = useModal();

  const isAuthorized = useAuthorization(["ADMIN", "DISP"]);
  const canDelete = useAuthorization(["ADMIN"]);
  const [updateBooking, { loading }] = useMutation(UPDATE_BOOKING_MUTATION, {
    refetchQueries: [refetchQueries || "ALL_BOOKINGS_QUERY"]
  });
  const [deleteBooking] = useMutation(DELETE_BOOKING_MUTATION, {
    refetchQueries: [refetchQueries || "ALL_BOOKINGS_QUERY"]
  });

  const [state, setState] = useState({
    BookingDate: new Date().toCorrectDateString()
  });

  // update state when initial state updates
  useEffect(() => {
    if (initialState) {
      setState(prev => ({
        ...prev,
        ...initialState,
        PickupDate: moment(initialState.PickupDate).format("YYYY-MM-DD"),
        CreatedDateTime: initialState?.CreatedDateTime
          ? moment(
              initialState.CreatedDateTime.slice(
                0,
                initialState.CreatedDateTime.lastIndexOf(".")
              )
            ).format("MM/DD/YY, HH:mm")
          : ""
        // CreatedDateTime: moment(initialState.CreatedDateTime).format(
        //   "YYYY-MM-DDThh:mm:ss"
        // )
      }));
    }
  }, [initialState]);

  const handleChange = e => {
    setState(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (!state.BookingCodeRecIdRef || state.BookingCodeRecIdRef === "0") {
      return toast.warning("Select a booking code");
    }
    try {
      await updateBooking({
        variables: {
          data: {
            EmployeeRecIdRef: parseInt(state.EmployeeRecIdRef),
            OperatorRecIdRef: parseInt(state.OperatorRecIdRef),
            BookingCodeRecIdRef: parseInt(state.BookingCodeRecIdRef),
            Comment: state.Comment,
            PickupDate: getUTCFormattedDate(
              moment(state.PickupDate).toISOString()
            ),
            BookingDate: getUTCFormattedDate(state.BookingDate),
            BookedTrip: state.BookedTrip
          },
          where: {
            RecId: parseInt(state.RecId)
          }
        }
      });
      toast.success("Booking Record updated.");
      onComplete();
    } catch (err) {
      toast.error("Error updating Booking Record.");
    }
  };
  useEffect(() => {
    if (user && user.RecId) {
      setState(prev => ({ ...prev, OperatorRecIdRef: user.RecId }));
    }
  }, [user]);

  const [isReversible, reverseBookingCode] = useBookingCodeReversible(
    state.BookingCodeRecIdRef
  );
  const hasBeenReversed = state?.Reversed;

  const handleReverseBooking = () => {
    setModalOpen(
      true,
      <AddBookingForm
        bookingCodeWhere={{
          RecId: reverseBookingCode
        }}
        refetchQueries={refetchQueries}
        state={{
          EmployeeRecIdRef: state.EmployeeRecIdRef,
          PickupDate: state.PickupDate,
          BookingDate: state.BookingDate,
          BookingCodeRecIdRef: reverseBookingCode
        }}
        onComplete={async () => {
          await updateBooking({
            variables: {
              data: {
                Reversed: true
              },
              where: {
                RecId: parseInt(state.RecId)
              }
            },
            refetchQueries: [refetchQueries || "ALL_BOOKINGS_QUERY"]
          });
          onComplete();
        }}
      />
    );
  };

  const handleDeleteBooking = async e => {
    e.preventDefault();
    try {
      await deleteBooking({
        variables: {
          where: {
            RecId: parseInt(state.RecId)
          }
        },
        refetchQueries: [refetchQueries || "ALL_BOOKINGS_QUERY"]
      });
      toast.success("Booking Record deleted.");
      onComplete();
    } catch (err) {
      toast.error("Error deleting Booking Record.");
    }
  };

  return (
    <form id="edit-booking-form" onSubmit={handleSubmit}>
      <Fieldset disabled={!isAuthorized} loading={loading}>
        <BookingForm
          bookingCodeWhere={bookingCodeWhere}
          isBookable={isBookable}
          readOnly={!isAuthorized}
          state={state}
          onChange={handleChange}
        />
      </Fieldset>
      <hr />
      <Field kind="group">
        {(isAuthorized || isReversible) && (
          <Control>
            <Button color="success" form="edit-booking-form" type="submit">
              Save
            </Button>
          </Control>
        )}
        <Control expanded>
          <Button type="button" onClick={() => onComplete(false)}>
            Cancel
          </Button>
        </Control>
        {isReversible && (
          <Control>
            <Button
              color="info"
              disabled={hasBeenReversed}
              title={
                hasBeenReversed
                  ? "This entry has already been reversed."
                  : "Reverse entry"
              }
              onClick={handleReverseBooking}
            >
              Reverse
            </Button>
          </Control>
        )}
        {canDelete && (
          <Control>
            <Button color="danger" onClick={handleDeleteBooking}>
              Delete
            </Button>
          </Control>
        )}
      </Field>
    </form>
  );
};

EditBookingForm.propTypes = {
  state: PropTypes.object,
  onComplete: PropTypes.func,
  isBookable: PropTypes.bool,
  refetchQueries: PropTypes.string,
  bookingCodeWhere: PropTypes.object
};
export default EditBookingForm;
